import {
  Document,
  Font,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from '@react-pdf/renderer'

import MuseoSans300 from '@/assets/museo-sans/MuseoSansCyrl-300.ttf'
import MuseoSans500 from '@/assets/museo-sans/MuseoSansCyrl-500.ttf'
import MuseoSans700 from '@/assets/museo-sans/MuseoSansCyrl-700.ttf'
import type { InvoiceData } from '@/types'
import { getVatAmount, withVat } from '@/utils/price/vat'

import {
  formatInvoiceDeliveryTime,
  formatInvoiceIssueDate,
  formatInvoiceMoney,
  formatInvoiceProductName,
  formatInvoiceQuantity,
  getPrintLogo,
  getPrintStamp,
  INVOICE_VALIDITY_TEXT,
  type InvoiceType,
  isRrcInvoice,
  numberToWordsRu,
} from '../invoiceUtils'

Font.register({
  family: 'Museo Sans Cyrl',
  fonts: [
    { src: MuseoSans300, fontWeight: 300 },
    { src: MuseoSans500, fontWeight: 500 },
    { src: MuseoSans700, fontWeight: 700 },
  ],
})

const formatAddressLine = (parts: (string | undefined | null | false)[]) =>
  parts.filter((part) => part && part !== '').join(', ')

const s = StyleSheet.create({
  page: {
    padding: 35,
    fontFamily: 'Museo Sans Cyrl',
    fontSize: 8.5,
    color: '#000',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 10,
    marginBottom: 0,
  },
  logo: { width: 75 },
  title: {
    fontSize: 14,
    fontWeight: '700',
    marginBottom: 15,
    textAlign: 'left',
  },

  row: { flexDirection: 'row', marginBottom: 8 },
  label: {
    width: 75,
    fontWeight: 500,
  },
  content: {
    flex: 1,
    lineHeight: 1.5,
    fontWeight: 400,
    fontSize: 10,
  },

  protocolPhrase: {
    marginTop: 15,
    fontWeight: 400,
    fontSize: 11,
  },

  commentLabel: { fontWeight: 400, fontSize: 11, marginTop: 5 },
  commentValue: { fontWeight: 700, fontSize: 11 },

  table: {
    marginTop: 15,
    borderStyle: 'solid',
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
  },
  tRow: { flexDirection: 'row', minHeight: 18, alignItems: 'stretch' },
  tHeader: {
    backgroundColor: '#f5f5f5',
    fontWeight: 'bold',
    textAlign: 'center',
  },
  tCell: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 3,
    justifyContent: 'center',
  },
  cellRight: { textAlign: 'right' },
  cellCenter: { textAlign: 'center' },

  totalsBlock: { marginTop: 10, lineHeight: 1.5 },
  totalBold: { fontWeight: 'bold', fontSize: 10 },
  validityText: { marginTop: 5 },

  signatureSection: {
    position: 'relative',
    marginTop: 50,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  },
  signatureLine: { borderBottom: 1, width: 150, marginBottom: 5 },
  signatureText: { marginBottom: 15 },
  signatureCaption: {
    position: 'absolute',
    top: 8,
    left: 0,
    right: 0,
    fontSize: 7,
    textAlign: 'center',
  },

  stampWrapper: {
    position: 'absolute',
    left: 40,
    top: -40,
    width: 450,
  },
  stampImg: {
    width: '100%',
  },
})

export const PdfInvoiceTemplate = ({
  data,
  type,
  issuedAt,
}: {
  data: InvoiceData | null
  type: InvoiceType
  issuedAt: Date
}) => {
  if (!data || !data.orderDetails) {
    return (
      <Document>
        <Page size="A4" style={s.page}>
          <Text>Данные подготавливаются...</Text>
        </Page>
      </Document>
    )
  }

  const { orderDetails, supplier, supplierBank, client, clientBank, payer } =
    data
  const isRRC = isRrcInvoice(type)
  const order = orderDetails.orderModel
  const logo = getPrintLogo()
  const stamp = getPrintStamp()

  let totalAmount = 0
  let totalVat = 0
  let totalFinal = 0

  const rows = (orderDetails.products || [])
    .map((product) => {
      if (product.status === 'REJECTED') return null

      let price, amount, vat, total
      if (isRRC) {
        const basePrice = product.retailPrice || 0
        amount = basePrice * product.quantity
        vat = getVatAmount(amount)
        total = amount + vat
        price = withVat(basePrice)
      } else {
        price = product.price
        amount = product.amount
        vat = product.vatAmount
        total = product.total
      }

      totalAmount += amount
      totalVat += vat
      totalFinal += total

      return {
        productName: formatInvoiceProductName(
          product.nomenclatureDto?.article,
          product.nomenclatureDto?.description,
          type
        ),
        deliveryTime: formatInvoiceDeliveryTime(
          product.storeShortModel?.etaMaxDays
        ),
        quantity: product.quantity,
        price,
        amount,
        vat,
        total,
      }
    })
    .filter((row): row is NonNullable<typeof row> => row !== null)

  return (
    <Document>
      <Page size="A4" style={s.page}>
        <View style={s.header}>
          {logo && <Image src={logo} style={s.logo} />}
        </View>

        <Text style={s.title}>
          Счет на оплату № {order?.code || ''} от{' '}
          {formatInvoiceIssueDate(issuedAt)}
        </Text>

        <View style={s.row}>
          <Text style={s.label}>Поставщик:</Text>
          <Text style={s.content}>
            {formatAddressLine([
              supplier?.vatNumber
                ? `${supplier.fullLegalName}, УНП ${supplier.vatNumber}`
                : supplier?.description,
              supplier?.legalAddress && `юр. адрес: ${supplier.legalAddress}`,
              supplierBank?.iban && `р/с № ${supplierBank.iban}`,
              supplierBank?.bankDto?.description &&
                `в ${supplierBank.bankDto.description}`,
              supplierBank?.bankDto?.code && `БИК ${supplierBank.bankDto.code}`,
              supplier?.phoneNumber && `тел. ${supplier.phoneNumber}`,
              supplier?.email && `e-mail: ${supplier.email}`,
            ])}
          </Text>
        </View>

        <View style={s.row}>
          <Text style={s.label}>Плательщик:</Text>
          <Text style={s.content}>
            {isRRC
              ? formatAddressLine([
                  payer?.description,
                  payer?.address && `адрес: ${payer.address}`,
                  payer?.ownerName && `владелец: ${payer.ownerName}`,
                  payer?.ownerPhoneNumber && `тел. ${payer.ownerPhoneNumber}`,
                ])
              : formatAddressLine([
                  client?.vatNumber
                    ? `${client.fullLegalName || client.description}, УНП ${client.vatNumber}`
                    : client?.description || client?.shortLegalName,
                  client?.legalAddress && `юр. адрес: ${client.legalAddress}`,
                  clientBank?.iban && `р/с № ${clientBank.iban}`,
                  clientBank?.bankDto?.description &&
                    `в ${clientBank.bankDto.description}`,
                  clientBank?.bankDto?.code && `БИК ${clientBank.bankDto.code}`,
                  client?.phoneNumber && `тел. ${client.phoneNumber}`,
                  client?.email && `e-mail: ${client.email}`,
                ])}
          </Text>
        </View>

        {order?.contractShortModel && (
          <Text style={s.protocolPhrase}>
            Договор № {order.contractShortModel.number} от{' '}
            {order.contractShortModel.creationDate
              ? new Date(
                  order.contractShortModel.creationDate
                ).toLocaleDateString('ru-RU')
              : ''}
          </Text>
        )}

        <Text style={s.protocolPhrase}>
          Счет на оплату является протоколом согласования цен
        </Text>

        {order?.comment && (
          <Text style={s.commentLabel}>
            Комментарий: <Text style={s.commentValue}>{order.comment}</Text>
          </Text>
        )}

        <View style={s.table}>
          {isRRC ? (
            <>
              <View style={[s.tRow, s.tHeader]}>
                <Text style={[s.tCell, { width: '50%' }]}>Товар</Text>
                <Text style={[s.tCell, { width: '14.29%' }]}>Кол-во</Text>
                <Text style={[s.tCell, { width: '14.29%' }]}>Цена</Text>
                <Text style={[s.tCell, { width: '21.42%' }]}>Всего</Text>
              </View>

              {rows.map((row, i) => (
                <View style={s.tRow} key={i}>
                  <Text style={[s.tCell, { width: '50%' }]}>
                    {row.productName}
                  </Text>
                  <Text style={[s.tCell, s.cellRight, { width: '14.29%' }]}>
                    {formatInvoiceQuantity(row.quantity)}
                  </Text>
                  <Text style={[s.tCell, s.cellRight, { width: '14.29%' }]}>
                    {formatInvoiceMoney(row.price)}
                  </Text>
                  <Text style={[s.tCell, s.cellRight, { width: '21.42%' }]}>
                    {formatInvoiceMoney(row.total)}
                  </Text>
                </View>
              ))}

              <View style={[s.tRow, { fontWeight: 'bold' }]}>
                <Text style={[s.tCell, s.cellRight, { width: '78.58%' }]}>
                  ИТОГО:
                </Text>
                <Text style={[s.tCell, s.cellRight, { width: '21.42%' }]}>
                  {formatInvoiceMoney(totalFinal)}
                </Text>
              </View>
            </>
          ) : (
            <>
              <View style={[s.tRow, s.tHeader]}>
                <Text style={[s.tCell, { width: '28%' }]}>Товар</Text>
                <Text style={[s.tCell, { width: '8%' }]}>Кол-во</Text>
                <Text style={[s.tCell, { width: '15%' }]}>
                  {'Срок доставки\n(банковских)'}
                </Text>
                <Text style={[s.tCell, { width: '9%' }]}>Цена</Text>
                <Text style={[s.tCell, { width: '9%' }]}>Сумма</Text>
                <Text style={[s.tCell, { width: '8%' }]}>Ст. НДС</Text>
                <Text style={[s.tCell, { width: '10%' }]}>Сумма НДС</Text>
                <Text style={[s.tCell, { width: '13%' }]}>Всего</Text>
              </View>

              {rows.map((row, i) => (
                <View style={s.tRow} key={i}>
                  <Text style={[s.tCell, { width: '28%' }]}>
                    {row.productName}
                  </Text>
                  <Text style={[s.tCell, s.cellRight, { width: '8%' }]}>
                    {formatInvoiceQuantity(row.quantity)}
                  </Text>
                  <Text style={[s.tCell, s.cellCenter, { width: '15%' }]}>
                    {row.deliveryTime}
                  </Text>
                  <Text style={[s.tCell, s.cellRight, { width: '9%' }]}>
                    {formatInvoiceMoney(row.price)}
                  </Text>
                  <Text style={[s.tCell, s.cellRight, { width: '9%' }]}>
                    {formatInvoiceMoney(row.amount)}
                  </Text>
                  <Text style={[s.tCell, s.cellCenter, { width: '8%' }]}>
                    20%
                  </Text>
                  <Text style={[s.tCell, s.cellRight, { width: '10%' }]}>
                    {formatInvoiceMoney(row.vat)}
                  </Text>
                  <Text style={[s.tCell, s.cellRight, { width: '13%' }]}>
                    {formatInvoiceMoney(row.total)}
                  </Text>
                </View>
              ))}

              <View style={[s.tRow, { fontWeight: 'bold' }]}>
                <Text style={[s.tCell, s.cellRight, { width: '60%' }]}>
                  ИТОГО:
                </Text>
                <Text style={[s.tCell, s.cellRight, { width: '9%' }]}>
                  {formatInvoiceMoney(totalAmount)}
                </Text>
                <Text style={[s.tCell, s.cellCenter, { width: '8%' }]}>X</Text>
                <Text style={[s.tCell, s.cellRight, { width: '10%' }]}>
                  {formatInvoiceMoney(totalVat)}
                </Text>
                <Text style={[s.tCell, s.cellRight, { width: '13%' }]}>
                  {formatInvoiceMoney(totalFinal)}
                </Text>
              </View>
            </>
          )}
        </View>

        <View style={s.totalsBlock}>
          {!isRRC && <Text>Сумма НДС: {numberToWordsRu(totalVat)}</Text>}
          <Text style={s.totalBold}>
            Всего к оплате: {numberToWordsRu(totalFinal)}
          </Text>
          <Text style={s.validityText}>{INVOICE_VALIDITY_TEXT}</Text>
        </View>

        <View
          style={[
            s.signatureSection,
            isRRC ? { justifyContent: 'center' } : {},
          ]}
        >
          {isRRC ? (
            <View style={{ flexDirection: 'row', alignItems: 'flex-end' }}>
              <Text style={{ marginRight: 8, fontWeight: '700' }}>
                Руководитель
              </Text>
              <View style={s.signatureLine} />
              <View
                style={[
                  s.signatureLine,
                  { marginLeft: 20, position: 'relative' },
                ]}
              >
                <Text style={s.signatureCaption}>расшифровка подписи</Text>
              </View>
            </View>
          ) : (
            <View style={s.stampWrapper}>
              {stamp && <Image src={stamp} style={s.stampImg} />}
            </View>
          )}
        </View>
      </Page>
    </Document>
  )
}
